// NOTE: This file intentionally avoids JSX for React Three Fiber intrinsic
// elements because the dev-time "visual-edits" babel plugin injects custom
// `x-*` attributes onto every JSX element. R3F tries to apply those props to
// the underlying three.js objects and throws. Using React.createElement keeps
// the same component tree without triggering the babel transform.
import React, { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Environment, MeshDistortMaterial } from "@react-three/drei";
import * as THREE from "three";
import { useTheme } from "../contexts/ThemeContext";

const h = React.createElement;

function FloatingKnot({ mouse, isDark }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.getElapsedTime();
    ref.current.rotation.x = THREE.MathUtils.lerp(
      ref.current.rotation.x,
      mouse.current.y * 0.4 + Math.sin(t * 0.2) * 0.15,
      0.05,
    );
    ref.current.rotation.y = THREE.MathUtils.lerp(
      ref.current.rotation.y,
      mouse.current.x * 0.6 + t * 0.05,
      0.05,
    );
  });

  return h(
    Float,
    { speed: 1.1, rotationIntensity: 0.4, floatIntensity: 0.8 },
    h(
      "mesh",
      { ref, scale: 1.6, position: [0, 0, 0] },
      h("torusKnotGeometry", { args: [1, 0.28, 220, 32, 2, 3] }),
      h(MeshDistortMaterial, {
        color: isDark ? "#0a0a0a" : "#d4d4d8",
        roughness: isDark ? 0.25 : 0.3,
        metalness: isDark ? 0.9 : 0.85,
        clearcoat: 1,
        clearcoatRoughness: 0.1,
        distort: 0.18,
        speed: 1.2,
        envMapIntensity: isDark ? 1.4 : 1.1,
      }),
    ),
  );
}

function OrbitingSpheres({ isDark }) {
  const group = useRef();
  useFrame((state) => {
    if (group.current) {
      group.current.rotation.y = state.clock.getElapsedTime() * 0.08;
      group.current.rotation.x =
        Math.sin(state.clock.getElapsedTime() * 0.1) * 0.2;
    }
  });
  const items = [
    { p: [3.2, 1.4, -1], s: 0.18 },
    { p: [-3.6, -1.1, -2], s: 0.12 },
    { p: [2.1, -2.0, 1.5], s: 0.09 },
    { p: [-2.8, 1.8, 1.2], s: 0.14 },
    { p: [0, 2.8, -2.2], s: 0.1 },
  ];
  return h(
    "group",
    { ref: group },
    items.map((it, i) =>
      h(
        "mesh",
        { key: i, position: it.p },
        h("icosahedronGeometry", { args: [it.s, 1] }),
        h("meshStandardMaterial", {
          color: isDark ? "#e2e8f0" : "#52525b",
          metalness: 1,
          roughness: 0.2,
          emissive: isDark ? "#ffffff" : "#000000",
          emissiveIntensity: 0.05,
        }),
      ),
    ),
  );
}

function Scene({ mouse, isDark }) {
  const bg = isDark ? "#050505" : "#fafafa";
  return h(
    React.Fragment,
    null,
    h("color", { attach: "background", args: [bg] }),
    h("fog", { attach: "fog", args: [bg, 6, 14] }),
    h("ambientLight", { intensity: isDark ? 0.15 : 0.5 }),
    h("directionalLight", {
      position: [6, 6, 4],
      intensity: isDark ? 1.4 : 1.2,
      color: "#ffffff",
    }),
    h("directionalLight", {
      position: [-5, -2, 3],
      intensity: 0.6,
      color: isDark ? "#9ca3af" : "#a1a1aa",
    }),
    h("pointLight", { position: [0, 0, 4], intensity: 0.6, color: "#ffffff" }),
    h(
      Suspense,
      { fallback: null },
      h(FloatingKnot, { mouse, isDark }),
      h(OrbitingSpheres, { isDark }),
      h(Environment, { preset: isDark ? "city" : "studio" }),
    ),
  );
}

export default function HeroCanvas() {
  const mouse = useRef({ x: 0, y: 0 });
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const onPointer = (e) => {
    const x = (e.clientX / window.innerWidth) * 2 - 1;
    const y = -(e.clientY / window.innerHeight) * 2 + 1;
    mouse.current.x = x;
    mouse.current.y = y;
  };

  return (
    <div
      className="absolute inset-0 -z-0"
      onPointerMove={onPointer}
      data-testid="hero-canvas"
      aria-hidden="true"
    >
      <Canvas
        key={theme /* force-remount on theme change so fog/background apply */}
        dpr={[1, 1.6]}
        camera={{ position: [0, 0, 5.2], fov: 45 }}
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: "high-performance",
        }}
      >
        <Scene mouse={mouse} isDark={isDark} />
      </Canvas>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: isDark
            ? "radial-gradient(ellipse at center, transparent 40%, rgba(5,5,5,0.85) 85%)"
            : "radial-gradient(ellipse at center, transparent 40%, rgba(250,250,250,0.85) 85%)",
        }}
      />
    </div>
  );
}
